/* eslint-disable @typescript-eslint/no-explicit-any -- Type guards require any to provide type narrowing from untyped input */
import type React from 'react';

type ReactComponent = React.FC<any> | React.ComponentClass<any, any>;

/**
 * Checks if a given value is a function component.
 */
export const isFunctionComponent = (
  component: any
): component is React.FC<any> => {
  return typeof component === 'function';
};

/**
 * Checks if a given value is a class component.
 */
export const isClassComponent = (
  component: any
): component is React.ComponentClass<any, any> => {
  return (
    typeof component === 'function' &&
    component.prototype &&
    (!!component.prototype.isReactComponent || !!component.prototype.render)
  );
};

/**
 * Checks if a given value is a forward ref component.
 */
export const isForwardRefComponent = (
  component: any
): component is React.ForwardRefExoticComponent<any> => {
  return (
    typeof component === 'object' &&
    component !== null &&
    component.$$typeof.toString() === 'Symbol(react.forward_ref)'
  );
};

/**
 * Checks if a given value is a valid React component.
 */
export const isReactComponent = (
  component: any
): component is ReactComponent => {
  return (
    isFunctionComponent(component) ||
    isForwardRefComponent(component) ||
    isClassComponent(component)
  );
};

/**
 * Checks if a given value is a valid React node (can be rendered).
 */
export const isReactNode = (value: any): value is React.ReactNode => {
  if (value === null || value === undefined) {
    return true;
  }
  if (
    typeof value === 'string' ||
    typeof value === 'number' ||
    typeof value === 'boolean'
  ) {
    return true;
  }
  // Validate React elements by checking for Symbol(react.element) or Symbol(react.fragment)
  if (typeof value === 'object' && value !== null && '$$typeof' in value) {
    const typeofValue = value.$$typeof;
    // Must be a Symbol, not just any object with $$typeof property
    return typeof typeofValue === 'symbol';
  }
  // Validate arrays by checking all elements
  if (Array.isArray(value)) {
    return value.every(isReactNode);
  }
  return false;
};
